import React, { FC, useContext, useEffect, useState } from 'react';
import { Modal, Button, Input, Alert, Copy } from 'esa-ui-library';
import { AuthContext } from '../../../../../logic';
import { setupTwoStepKey } from '../../../../../api';
import styles from './TwoStepAuthModal.module.scss';

interface TwoStepAuthModalProps {
	onClose: () => void;
}

const TwoStepAuthModal: FC<TwoStepAuthModalProps> = ({ onClose }): JSX.Element => {
	const { token, logout } = useContext(AuthContext);
	const [secret, setSecret] = useState<string>('');
	const [uri, setUri] = useState<string>('');
	const [code, setCode] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(true);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [success, setSuccess] = useState<boolean>(false);
	const [copied, setCopied] = useState<boolean>(false);

	const fetchSecret = async () => {
		setLoading(true);
		setError('');
		try {
			const res: any = await setupTwoStepKey({ token });
			if (res?.secret && res?.uri) {
				setSecret(res.secret);
				setUri(res.uri);
			} else if (res?.data?.secret && res?.data?.uri) {
				setSecret(res.data.secret);
				setUri(res.data.uri);
			} else if (res?.secret) {
				setSecret(res.secret);
				setUri(res.uri || '');
			} else {
				throw new Error('Invalid response from server');
			}
		} catch (err: any) {
			setError(err?.message || 'Failed to request two-step authentication secret');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSecret();
	}, [token]);

	const handleCopy = async () => {
		if (!uri) return;
		try {
			await navigator.clipboard.writeText(uri);
			setCopied(true);
			setTimeout(() => setCopied(false), 2500);
		} catch (err) {
			// Fallback if navigator.clipboard fails
		}
	};

	const handleVerify = async () => {
		if (!code.trim()) {
			setError('Please enter the verification code');
			return;
		}

		setSubmitting(true);
		setError('');
		try {
			const res: any = await setupTwoStepKey({
				token,
				secret,
				two_step_code: code.trim(),
			});
			if (res?.resultCode === 0 || res?.data?.resultCode === 0 || !res?.resultCode) {
				setSuccess(true);
			} else {
				throw new Error(res?.errorMessage || 'Failed to verify code');
			}
		} catch (err: any) {
			setError(err?.message || 'Invalid verification code. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	const handleDone = () => {
		onClose();
		logout();
	};

	if (success) {
		return (
			<Modal
				title='Two-Step Authentication'
				primary
				submitLabel='Log In'
				onSubmit={handleDone}
				onCancel={handleDone}
				dataId='two-step-success-modal'
			>
				<div className={styles.TwoStepModal}>
					<Alert
						text='Two-step authentication has been successfully set up. All active sessions have been invalidated. Please log in again.'
						className={styles.Alert}
					/>
				</div>
			</Modal>
		);
	}

	return (
		<Modal
			title='Set Up Two-Step Authentication'
			primary
			loading={loading || submitting}
			submitLabel={submitting ? 'Verifying...' : 'Verify & Enable'}
			onSubmit={handleVerify}
			onCancel={onClose}
			dataId='two-step-setup-modal'
		>
			<div className={styles.TwoStepModal}>
				{loading ? (
					<p className={styles.Loading}>Generating two-step authentication key...</p>
				) : (
					<>
						{error && <Alert danger text={error} className={styles.Alert} />}
						{uri ? (
							<>
								<p className={styles.Description}>
									1. Add the OTP URI below to your authenticator app or password manager:
								</p>
								<div className={styles.UriContainer}>
									<div className={styles.UriBox}>{uri}</div>
									<Button teritary small onClick={handleCopy} className={styles.CopyButton} dataId='copy-uri-button'>
										<Copy className={styles.CopyIcon} />
										{copied ? 'Copied!' : 'Copy URI'}
									</Button>
								</div>
								<p className={styles.Description}>
									2. Enter the verification code generated by your authenticator app:
								</p>
								<div className={styles.CodeInput}>
									<Input
										type='text'
										label='Verification Code'
										placeholder='Enter 6-digit code'
										value={code}
										onChange={(val) => {
											setCode(val);
											if (error) setError('');
										}}
										onEnter={handleVerify}
										dataId='two-step-code-input'
										autoFocus
									/>
								</div>
							</>
						) : (
							<div className={styles.RetryContainer}>
								<Button primary small onClick={fetchSecret} title='Retry'>
									Retry
								</Button>
							</div>
						)}
					</>
				)}
			</div>
		</Modal>
	);
};

export { TwoStepAuthModal };
